/**
 * UI-8e Task 7 — FlagAccordionItem.
 *
 * One row in the FeatureFlagsPage accordion. The header summarises the
 * flag (key, global ON/OFF + rollout, override count) and lets the
 * operator delete the entire flag (via TypeToConfirmDialog). The expanded
 * body exposes:
 *
 *   - Global inline editor — enabled toggle + rollout slider + Save.
 *     Save calls `useFeatureFlags().setGlobal(flagKey, payload)`.
 *   - Override rows — each with its own inline editor + × remove button.
 *     Save calls `setOverride`; × calls `removeOverride`.
 *   - "+ Add tenant override" button → opens AddOverrideDialog.
 *
 * Why a hand-rolled accordion (not PrimeReact's Accordion)? PlatformLayout
 * uses a custom collapse pattern for the same reason — Prime's Accordion
 * lacks per-section custom headers and keeps the "expanded" state on its
 * own controller. Rolling our own keeps the row composable and matches
 * sibling components elsewhere on the platform surface.
 *
 * InlineFlagEditor is defined inline because the component is used twice
 * in this file only (global + each override). Pulling it into a shared
 * module would add an import without any reuse upside.
 */
import { useEffect, useState, type JSX } from 'react'
import { Slider } from 'primereact/slider'
import { Trash2, ChevronRight, ChevronDown, Plus, X } from 'lucide-react'

import {
  useFeatureFlags,
  type GroupedFlag,
  type FlagValue,
  type OverrideRow,
} from '../../hooks/useFeatureFlags.js'
import { AddOverrideDialog } from './AddOverrideDialog.js'
import { TypeToConfirmDialog } from '../admin/TypeToConfirmDialog.js'

// ---------------------------------------------------------------------------
// InlineFlagEditor
// ---------------------------------------------------------------------------

interface InlineFlagEditorProps {
  value: FlagValue
  onSave: (next: FlagValue) => Promise<void>
  /** Identifies the editor for test selectors (e.g. 'global', 'override-acme'). */
  testId: string
}

function InlineFlagEditor({
  value,
  onSave,
  testId,
}: InlineFlagEditorProps): JSX.Element {
  const [enabled, setEnabled] = useState(value.enabled)
  const [rolloutPct, setRolloutPct] = useState(value.rolloutPct)
  const [saving, setSaving] = useState(false)

  // If the parent reloads with fresh data (after a refetch), sync local state.
  useEffect(() => {
    setEnabled(value.enabled)
    setRolloutPct(value.rolloutPct)
  }, [value.enabled, value.rolloutPct])

  const dirty =
    enabled !== value.enabled || rolloutPct !== value.rolloutPct

  async function handleSave(): Promise<void> {
    if (!dirty) return
    setSaving(true)
    try {
      await onSave({ enabled, rolloutPct })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid={`inline-editor-${testId}`} className="flex flex-wrap items-center gap-3">
      <div className="flex gap-1">
        {[
          { label: 'OFF', value: false },
          { label: 'ON', value: true },
        ].map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={() => setEnabled(opt.value)}
            className={`px-3 py-1.5 text-[10px] font-semibold rounded-sm transition-all border ${
              enabled === opt.value
                ? opt.value ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
                : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Slider
          value={rolloutPct}
          onChange={(e) => { if (typeof e.value === 'number') setRolloutPct(e.value) }}
          min={0} max={100}
          style={{ width: '6rem' }}
        />
        <span className="w-8 text-[10px] font-semibold text-gray-500">{rolloutPct}%</span>
      </div>
      <button
        onClick={() => void handleSave()}
        disabled={!dirty || saving}
        className="inline-flex items-center gap-1 bg-[#3454cf] hover:bg-[#2a45b0] disabled:bg-gray-200 text-white text-[10px] font-semibold px-2.5 py-1.5 rounded-sm transition-all"
      >
        {saving && <i className="pi pi-spin pi-spinner text-[8px]" />}
        Save
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// FlagAccordionItem
// ---------------------------------------------------------------------------

export interface FlagAccordionItemProps {
  flag: GroupedFlag
  /**
   * Called after any mutation succeeds. useFeatureFlags keeps its own state per
   * call, so the hook used in here refetches a private copy; the page that owns
   * the list passes its refetch so the list on screen updates immediately.
   */
  onChanged?: () => void | Promise<void>
}

export function FlagAccordionItem({
  flag,
  onChanged,
}: FlagAccordionItemProps): JSX.Element {
  const hook = useFeatureFlags()
  const refreshList = async (): Promise<void> => {
    await onChanged?.()
  }
  const setGlobal: typeof hook.setGlobal = async (...a) => {
    await hook.setGlobal(...a)
    await refreshList()
  }
  const setOverride: typeof hook.setOverride = async (...a) => {
    await hook.setOverride(...a)
    await refreshList()
  }
  const removeOverride: typeof hook.removeOverride = async (...a) => {
    await hook.removeOverride(...a)
    await refreshList()
  }
  const removeFlag: typeof hook.removeFlag = async (...a) => {
    await hook.removeFlag(...a)
    await refreshList()
  }
  const [expanded, setExpanded] = useState(false)
  const [addOverrideOpen, setAddOverrideOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const globalEnabled = flag.global?.enabled ?? false
  const globalPct = flag.global?.rolloutPct ?? 0
  const overrideCount = flag.overrides.length

  return (
    <>
      <div className="mb-2 overflow-hidden rounded-sm border border-gray-100 bg-white hover:shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-shadow">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <button
            type="button"
            aria-label="Toggle flag"
            aria-expanded={expanded}
            className="flex flex-1 items-center gap-3 text-left"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
            <code className="text-sm font-semibold text-gray-900">{flag.flagKey}</code>
          </button>
          <span className="flex items-center gap-3">
            {flag.global ? (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${globalEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {globalEnabled ? `ON ${globalPct}%` : 'OFF'}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700">
                No global
              </span>
            )}
            <span className="text-xs text-gray-400 font-medium">
              {overrideCount} override{overrideCount === 1 ? '' : 's'}
            </span>
            <button
              className="h-7 w-7 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-red-200 hover:text-red-600 hover:bg-red-50 transition-all"
              aria-label={`Delete flag ${flag.flagKey}`}
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </span>
        </div>

        {/* Body */}
        {expanded && (
          <div className="space-y-4 border-t border-gray-50 bg-gray-50/50 px-5 py-4">
            <div className="flex flex-col gap-2">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-400">Global</div>
              <InlineFlagEditor
                testId="global"
                value={{ enabled: globalEnabled, rolloutPct: globalPct }}
                onSave={(next) => setGlobal(flag.flagKey, next)}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-400">Tenant Overrides</div>
              {flag.overrides.length === 0 && (
                <span className="text-xs text-gray-400 italic pl-1">No tenant overrides.</span>
              )}
              {flag.overrides.map((ov: OverrideRow) => (
                <div
                  key={ov.id}
                  className="flex flex-wrap items-center gap-3 rounded-sm border border-gray-100 bg-white px-3 py-2"
                  data-testid={`override-row-${ov.tenantSlug}`}
                >
                  <code className="text-sm font-medium text-gray-700 min-w-[80px]">{ov.tenantSlug}</code>
                  <InlineFlagEditor
                    testId={`override-${ov.tenantSlug}`}
                    value={{ enabled: ov.enabled, rolloutPct: ov.rolloutPct }}
                    onSave={(next) => setOverride(flag.flagKey, ov.tenantId!, next)}
                  />
                  <button
                    className="h-6 w-6 flex items-center justify-center rounded-full border border-gray-100 text-gray-400 hover:border-red-200 hover:text-red-600 hover:bg-red-50 transition-all"
                    aria-label={`Remove override for ${ov.tenantSlug}`}
                    onClick={() => void removeOverride(flag.flagKey, ov.tenantId!)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <div>
                <button
                  onClick={() => setAddOverrideOpen(true)}
                  className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#3454cf] hover:text-[#2a45b0] px-2 py-1.5 rounded-sm border border-[#3454cf]/20 hover:border-[#3454cf]/40 transition-all bg-[#3454cf]/5 hover:bg-[#3454cf]/10"
                >
                  <Plus className="h-3 w-3" /> Add tenant override
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <AddOverrideDialog
        open={addOverrideOpen}
        flagKey={flag.flagKey}
        onClose={() => setAddOverrideOpen(false)}
        onSubmit={async (tenantId, v) => {
          await setOverride(flag.flagKey, tenantId, v)
        }}
      />

      <TypeToConfirmDialog
        open={deleteOpen}
        title={`Delete ${flag.flagKey}?`}
        message="Removes the global flag and all overrides. This cannot be undone."
        confirmText={flag.flagKey}
        onConfirm={() => { void (async () => { await removeFlag(flag.flagKey); setDeleteOpen(false) })() }}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  )
}

export default FlagAccordionItem
